import {
  ArrowLeftIcon,
  Button,
  Flex,
  ProviderConsumer as FluentUIThemeConsumer,
  Menu,
  Tooltip
} from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import '../../../../responsive.css';
import { appState, useSetState } from '../../../AppState';
import { TikitConfigState } from '../../../config/TikitConfiguration';
import { getCachedFeature } from '../../../shared/cache/FeatureCache';
import { CheckFeature } from '../../../shared/components/CheckFeature';
import { EditTicketApprovals } from '../../../shared/components/EditTicketApprovals/EditTicketApprovals';
import { TicketApprovalsList } from '../../../shared/components/EditTicketApprovals/TicketApprovalsList';
import { TicketApprovalsSkeleton } from '../../../shared/components/EditTicketApprovals/TicketApprovalsSkeleton';
import { EditTicketAttachments } from '../../../shared/components/EditTicketAttachments/EditTicketAttachments';
import { getAllMergedTicketIds } from '../../../shared/components/EditTicketRelateTickets/RelateTicketHelper';
import { TicketConversation } from '../../../shared/components/TicketConversation/TicketConversation';
import { TicketCustomFormSection } from '../../../shared/components/TicketCustomFormTab/TicketCustomFormSection';
import { ResolutionDetails } from '../../../shared/components/TicketResolutionNoteComponents/ResolutionDetails';
import { GroupChat } from '../../../shared/interfaces/ticket.interface';
import { graphService } from '../../../shared/services/graph.service';
import { platformService } from '../../../shared/services/platform.service';
import { ConfigurationName, LICENSE_FEATURE, MyWorkListViewType, PRODUCT_LICENSE, STATUS_GUID, TicketApprovalState, toastDefault, USER_TYPE } from '../../../shared/utils/constants';
import { retrieveTicketInformation } from '../../../shared/utils/propertyHelper';
import { EditTicketEvent, getCommentsCount, getStatusIdByGuid, loadFilters, mergedCollaboratorOrAffectedUsers, threedigitized, updateApproval, updateApprovalList } from '../../ticketHelper';
import { EditRequestDetailsSkeleton } from '../Skeletons/EditRequestDetailsSkeleton';
import { GroupUsersCard } from './GroupUsersCard/GroupUsersCard';
import { RequestDetailCard } from './RequestDetailCard/RequestDetailCard';
import { TicketPagePrint } from '../../TicketPagePrint/TicketPagePrint';

interface Props {
  ticketId: number;
  signInUser: SignedInUser;
  ticket: Ticket | null;
  isUserLicensed: boolean;
  setTicket: (value: Ticket | null) => void;
  setIsUserLicensed: (value: boolean | null) => void;
  toggleEditingToolbar?: (value: boolean) => void;
  isEditingOpen?: boolean;
  fileTriggerChannel?: string;
  setIsAuthorizedAccess?: (value: boolean) => void;
}

const api = new platformService();

export const EditRequestDetails = ({
  ticketId,
  signInUser,
  toggleEditingToolbar,
  isEditingOpen,
  fileTriggerChannel,
  setIsAuthorizedAccess,
  ticket,
  isUserLicensed,
  setTicket,
  setIsUserLicensed
}: Props) => {
  const currentState = appState();
  const setState = useSetState();
  const graphAPI = new graphService();
  let FAILEDAPICOUNT = 0;
  const showIsEditing = isEditingOpen ? `editting-state-form` : ``;
  const [isLoading, setIsLoading] = useState(true);
  const [loadingApprovals, setLoadingApprovals] = useState(true);
  const [menu, setMenu] = useState(0);
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const [approvals, setApprovlas] = useState([]);
  const [fileAttachments, setFileAttachments] = useState([]);
  const [commentsCount, setCommentsCount] = useState<number>(0);
  const [mergedTicketIds, setMergedTicketIds] = useState<number[]>([]);
  const [seeMoreCollaborators, setSeeMoreCollaborators] = useState(false);
  const [seeMoreAffectedUsers, setSeeMoreAffectedUsers] = useState(false);
  const [reloadConversation, setReloadConversation] = useState<boolean>(false);
  const onCommentUpdated = useCallback(() => {
    setReloadConversation(true);
    if (ticketId) api.getTicketFiles(ticketId).then((files) => setFileAttachments(files));
  }, [ticketId]);
  const [tikitConfig, setTikitConfig] = useState<TikitConfiguration>(TikitConfigState);
  const [isResolutionNoteEnabled, setIsResolutionNoteEnabled] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [_isGroupChatEnabled, setIsGroupChatEnabled] = useState(false);
  const [groupChat, setGroupChat] = useState<GroupChat>(null);
  
  const clickOnSeeMoreCollaboratorBtn = () => {
    setSeeMoreCollaborators(!seeMoreCollaborators);
  };

  const clickOnSeeMoreAffectedUsersBtn = () => {
    setSeeMoreAffectedUsers(!seeMoreAffectedUsers);
  };

  const [isApprovalsOpen, setIsApprovalsOpen] = useState(false);
  const [isLifeCycleEnabled, setIsLifeCycleEnabled] = useState<boolean>(false);
  const {t} = useTranslation();

  const loadFiltersData = useCallback(async () => {
    try {
      const newState = await loadFilters(api, graphAPI, currentState, setState);
      FAILEDAPICOUNT = 0;
      return newState;
    } catch (error) {
      if (++FAILEDAPICOUNT <= 5) loadFiltersData();
      console.error(error);
    }
  }, []);
  
  const itemRenderer = (MenuItem, props) => {
    const { tooltip = '', key, ...rest } = props;
    if (key === 'template' && (ticket.TemplateId || 0) <= 0)
      rest.disabled = true;

    return (
      <Tooltip content={tooltip} key={key}>
        <MenuItem {...rest} />
      </Tooltip>
    );
  };

  const _menuItems = [
    {
      key: 'activity',
      content: t('ticket-details.menu.activity'),
      tooltip: t('ticket-details.menu.tooltip'),
      children: itemRenderer
    }
  ];

  const getTicketData = async (id: number) => {
    try {
      setGroupChat(null);
      const isRelateTicketEnabled = await getCachedFeature(LICENSE_FEATURE.RelatedTickets);
      const results = await Promise.all([
        api.getTicket(id, '?$expand=CustomForms,AffectedUsers($select=PlatformUserId),TicketCollaborators($select=PlatformUserId),Status,Category,Priority,Assignee,Requester,FileAttachments,Comments,Template,TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($orderby=Order))'),
        api.getTicketFiles(id),
        isRelateTicketEnabled ? api.getAllMergedTickets() : Promise.resolve([]),
        getCachedFeature(PRODUCT_LICENSE.AnalystAccess),
        api.getTeamsBotConfiguration(),
        api.getSystemSetting(ConfigurationName.Resolution),
        getCachedFeature(LICENSE_FEATURE.ResolutionNote),
        getCachedFeature(LICENSE_FEATURE.GroupChat),
        api.getSystemSetting(ConfigurationName.GroupChat)
      ]);

       

      setState(prevState => {
        prevState.teamsConfig = results[4].data
        return prevState;
      });

      setIsUserLicensed(results[3]);

      if((currentState?.usersInRole?.length ?? 0) == 0)
        await loadFiltersData();

      const ticket = results[0].data;
      await retrieveTicketInformation(ticket, currentState, true, true);
      
      setTicket(ticket);
      setIsLoading(false);
      setFileAttachments(results[1]);

      const mergedTicketIdsList = isRelateTicketEnabled ? getAllMergedTicketIds(id, results[2]) : [];
      setMergedTicketIds(mergedTicketIdsList);
      if (mergedTicketIdsList && mergedTicketIdsList.length > 0) setTicketCommentsCount(id, mergedTicketIdsList);
      else setCommentsCount(ticket?.Comments?.length ?? 0);

      setTikitConfig({ ...results[5].data, IsResolutionNoteEnabled: results[5].data.IsEnabled });
      setIsResolutionNoteEnabled(results[6]);
      if (results[7]) {
        setIsGroupChatEnabled(results[8].data.IsEnabled);
        if (results[8].data.IsEnabled && ticket.GroupChatId) {
            let groupChat = await api.getGroupChat(ticket.GroupChatId);
            setGroupChat(groupChat.data);
        }         
      }
    } catch (error) {
      setIsLoading(false);
      const errCode = error['response']?.status;
      if(errCode == 404) setIsAuthorizedAccess(false);
      console.error(error);
    }
  };

  useEffect(()=>{
    if (ticket?.CustomForms?.length > 0) {
      _menuItems.push({
        key: 'customForm',
        content: t('ticket-details.edit-ticket-form.custom-form-answer.title'),
        tooltip: t('ticket-details.edit-ticket-form.custom-form-answer.tooltip'),
        children: itemRenderer
      });
    }
    if (isResolutionNoteEnabled && tikitConfig.IsResolutionNoteEnabled && ticket?.StatusId){
      const resolvedStatusId = getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.RESOLVED);
      const closedStatusId = getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.CLOSED);
      if ([resolvedStatusId,closedStatusId].includes(ticket.StatusId) || ticket.ResolutionId!=null){
        _menuItems.push({
          key: 'resolution',
          content: t('resolution-note.details-tab'),
          tooltip: t('resolution-note.details-tab'),
          children: itemRenderer
        });
      }
    }
    setMenuItems(_menuItems);
    setMenu(prev => prev < _menuItems.length ? prev : 0);
  },[isResolutionNoteEnabled, ticket]);

  const loadPage = async (id: number) => {
    setFileAttachments([]);
    setCommentsCount(0);
    setIsAttachmentOpen(false);
    setIsLoading(true);
    getTicketData(id);
    setIsApprovalsOpen(currentState.selectedTaskOrApproval?.type === MyWorkListViewType.Approvals);
    setState((prev) => ({ ...prev, selectedTaskOrApproval: undefined }));
  }

  const setTicketCommentsCount = async (id: number, mergedTicketIdsList: number[] = []) => {
    const ticketIds = [...mergedTicketIdsList, parseInt(id.toString())].join(',');
    const result = await api.getTicketComments(ticketIds);
    setCommentsCount(getCommentsCount(result));
  }

  const resetCommentsCount = () => {
    setTicketCommentsCount(ticket.Id, mergedTicketIds);
  }

  useEffect(() => {
    if (ticketId) loadPage(ticketId);
  }, [ticketId]);

  useEffect(() => {
    if (currentState.currentUserId && ticket) loadApprovals();
  }, [ticket]);

  const loadApprovals = async () => {
    try {
      setLoadingApprovals(true);
      const [isApprovalsEnabled, isLifecyclesEnabled] = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.Approvals),
        getCachedFeature(LICENSE_FEATURE.TicketLifecycle)
      ]);
      if (isApprovalsEnabled) {
        setApprovlas(await getPendingApprovals(isLifecyclesEnabled));
      }
      setIsLifeCycleEnabled(isLifecyclesEnabled);
      setLoadingApprovals(false);
    }
    catch (error) {
      setLoadingApprovals(false);
      console.error(error);
    }
  }

  const getPendingApprovals = async (isLifecyclesEnabled: boolean) => {
    let isRequester = ticket ? ticket.Requester?.Id==currentState.currentUserId : false;
    return await api.getApprovalsByUserId(currentState.currentUserId, isLifecyclesEnabled, ticketId, isRequester);
  }

  const menuIndexChange = (_e, props: any) => {
    setMenu(props.activeIndex);
  };

  const ContentMenu = () => (
    <Menu
      defaultActiveIndex={menu}
      items={menuItems}
      underlined
      primary
      onActiveIndexChange={menuIndexChange}
      className='print-hidden'
    />
  );

  const onInteraction = async action => {
    if (action.event === EditTicketEvent.SaveSuccess)
      getTicketData(ticketId);
    if (action.event === 'onAttachmentsUpdate') {
      setReloadConversation(true);
      api.getTicketFiles(ticketId).then((files) => setFileAttachments(files));
    }
  };

  const updateApprovalStatus = async (approval: Approval, approverId: number, status: TicketApprovalState) => {
    try {
      const result = await updateApproval(api, approval, approverId, status, t);
      result.IsError ? toast.error(result.Message, toastDefault) : toast.success(result.Message, toastDefault);
      refreshApprovalList(approval.Id, approverId, status, result.UpdatedStatus);
    } catch (e) {
      toast.error(t('ticket-details.edit-ticket-form.approval.error.update'), toastDefault);
      console.error(e);
    }
  }

  const refreshApprovalList = (approvalId: number, approverId: number, state: TicketApprovalState, updatedStatus: TicketApprovalState) => {
    const updatedApprovals = updateApprovalList(approvals, approverId, approvalId, state, updatedStatus);
    setApprovlas(updatedApprovals);
  }

  const getLatestDroppedIndex = (fileAttachments: any): number => {
    let idx = 1;
    for (const file of fileAttachments) {
      if (file.FileName.indexOf('image-') > -1) {
        const indexNumber = parseInt(file.FileName.substring(file.FileName.indexOf('image-') + 'image-'.length, file.FileName.indexOf('image-') + 'image-'.length + 3));
        if (indexNumber > idx)
          idx = indexNumber;
      }
    }
    return idx;
  }

  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <>
          {isLoading && <EditRequestDetailsSkeleton />}
          {!isLoading && (
            <>
              {ticket ? (
                <>
                  <Helmet>
                    <title>Ticket #{threedigitized(ticket?.Id)} - Tikit</title>
                  </Helmet>
                  <Flex.Item styles={{ flex: 1 }}>
                    <Flex column className="md:pt-5 md:px-5 md:pb-1">
                      <TicketPagePrint ticketState={{ticketId: ticket.Id, isSelectItem: true, isTicketLoaded: !isLoading}} ticket={ticket} globalTheme={globalTheme} isEndUser={true} />
                      <RequestDetailCard
                        ticketData={ticket} 
                        commentsCount={commentsCount}
                        attachmentsCount={fileAttachments?.filter((a) => a["IsPublic"]).length ?? 0}
                      />
                      <Flex.Item className='print-hidden'>
                        <ContentMenu />
                      </Flex.Item>
                      {menuItems[menu]?.key==="activity" && signInUser && (
                        <TicketConversation
                          isTicketMerged={ticket.IsMerged}
                          isTicketClosed={ticket.Closed}
                          isUserLicensed={isUserLicensed}
                          ticketId={ticketId}
                          assigneeId={ticket.AssigneeId}
                          requester={ticket.Requester}
                          signedInUser={signInUser}
                          isEndUserPage={true}
                          resetCommentsCount={resetCommentsCount}
                          priority={ticket.PriorityId} 
                          status={ticket.StatusId} 
                          dueDate={ticket.DueDate}        
                          ticketData={null} 
                          uploadedFiles={fileAttachments}
                          setUploadedFiles={setFileAttachments}
                          reloadConversation={reloadConversation}
                          setReloadConversation={setReloadConversation}
                          onCommentUpdated={onCommentUpdated}
                          globalTheme={globalTheme}   
                          droppedIndex={getLatestDroppedIndex(fileAttachments)}
                          groupChat={groupChat}
                        />
                      )}
                      {menuItems[menu]?.key==="resolution" && (
                          <div className='resolution-note-activity print-hidden' style={{background : globalTheme.siteVariables.theme===themeNames.Dark ? "var(--mgt-theme-background)" : "white"}}>
                            <ResolutionDetails
                              isEndUser={true}
                              ticketData={ticket}
                              resolutionNoteConfig={tikitConfig}
                              isDialog={false}
                            />
                          </div>
                        )}
                      {menuItems[menu]?.key==="customForm" && (
                        <TicketCustomFormSection
                            isEndUserPage={true}
                            customForms = {ticket.CustomForms} 
                            ticketId={ticket.Id} 
                            showUpdateAnswerButton={false} 
                            disabledFormInputs={true} />
                      )}
                    </Flex>
                  </Flex.Item>

                  <Flex.Item className={`collapsible-component ${showIsEditing} w-1/4 h-full overflow-auto pb-10`}>
                    <div className={`m-0 overflow-auto mobile-editform px-3 py-5`}>
                      <div className={`block md:hidden mb-3`}>
                        <Button
                          onClick={() => { toggleEditingToolbar(false); }}
                          className={`mb-1`}
                          content={<ArrowLeftIcon size="large" />}
                          text
                          iconOnly
                          style={{ color: globalTheme.siteVariables.colorScheme.brand.foregroundActive }}
                        />
                      </div>
                      
                      <CheckFeature featureName={LICENSE_FEATURE.AffectedUsers}>
                        {(ticket.AffectedUsers?.length > 0 || ticket.Requester) && (
                          <div>
                            <GroupUsersCard
                              title={t('requests.affected-users')}
                              usersList={mergedCollaboratorOrAffectedUsers(_.cloneDeep(ticket.AffectedUsers?.map(a => a.PlatformUser)), ticket.Requester, USER_TYPE.AFFECTED_USER)}
                              clickOnSeeMoreBtn={clickOnSeeMoreAffectedUsersBtn}
                              seeMore={seeMoreAffectedUsers}
                            />
                          </div>
                        )}
                      </CheckFeature>

                      <CheckFeature featureName={LICENSE_FEATURE.Collaborators}>
                        {(ticket.TicketCollaborators?.length > 0 ||
                          ticket.Assignee) && (
                          <div className={`mt-2`}>
                            <GroupUsersCard
                              title={t('requests.assigned-agents')}
                              usersList={mergedCollaboratorOrAffectedUsers(_.cloneDeep(ticket.TicketCollaborators?.map(a => a.PlatformUser)), ticket.Assignee, USER_TYPE.COLLABORATOR)}
                              clickOnSeeMoreBtn={clickOnSeeMoreCollaboratorBtn}
                              seeMore={seeMoreCollaborators}
                            />
                          </div>
                        )}
                      </CheckFeature>

                      <EditTicketAttachments
                        ticketId={ticketId}
                        globalTheme={globalTheme}
                        isOpen={isAttachmentOpen}
                        onCLose={value => {
                          setIsAttachmentOpen(value);
                        }}
                        primaryButtonColor={ globalTheme.siteVariables.colorScheme.brand.foreground1}
                        isSaving={false}
                        onInteraction={onInteraction}
                        fileTriggerChannel={fileTriggerChannel}
                        isEndUserPage={true}
                        isTicketClosed={ticket.Closed}
                        fileAttachments={fileAttachments}
                        teamsConfig={currentState.teamsConfig}
                        signedInUser={signInUser}
                        loadingTicketData={isLoading}
                      />

                      <CheckFeature featureName={LICENSE_FEATURE.Approvals}>
                        <EditTicketApprovals
                          ticketApprovalsCount={approvals?.length ?? 0}
                          isFormDisabled={ticket.Closed}
                          isApprovalsOpen={isApprovalsOpen}
                          setIsApprovalsOpen={setIsApprovalsOpen}
                          primaryButtonColor={globalTheme.siteVariables.colorScheme.brand.foreground1}
                          isEndUser={true}
                        />
                        {isApprovalsOpen && ((loadingApprovals || !currentState.currentUserId) ? (<TicketApprovalsSkeleton />) : (
                          <TicketApprovalsList 
                            ticketApprovals={approvals}
                            currentUserId={currentState.currentUserId}
                            isTicketClosed={ticket.Closed}
                            isEndUser={true}
                            updateApprovalStatus={updateApprovalStatus}
                            disableButonOnUpdate={true}
                          />
                        ))}
                      </CheckFeature>
                    </div>
                  </Flex.Item>
                </>
              ) : null}
            </>
          )}
        </>
      )}
    />
  );
};
